import {
  Box,
  Button,
  FormField,
  Heading,
  InputArea,
  MessageModalLayout,
  Modal,
  ModalMobileLayout,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { TLocalesKeys } from '../../../../types';
import { useIsSmallScreen } from '../../hooks';
import { AnswerFeedbackDataHooks } from './AnswerFeedback.dataHooks';

const INPUT_MAX_LENGTH = 1000;

type OtherReasonModalProps = {
  isOpen: boolean;
  onSubmit: (text?: string) => void;
  onClose: () => void;
};

export const OtherReasonModal: FC<OtherReasonModalProps> = React.memo((props) => {
  const isSmallScreen = useIsSmallScreen();
  const { isOpen, onSubmit, onClose } = props;
  const [inputAreaValue, setInputAreaValue] = useState('');
  const [hasLimitError, setHasLimitError] = useState(false);
  const { t } = useTranslation();

  const handleModalSubmit = useCallback(() => {
    onSubmit(inputAreaValue);
  }, [inputAreaValue, onSubmit]);

  const primaryButtonProps = useMemo(() => {
    return {
      disabled: !inputAreaValue || hasLimitError,
    };
  }, [inputAreaValue, hasLimitError]);

  const handleInputChange = useCallback((e) => {
    setInputAreaValue(e.target.value);
  }, []);

  useEffect(() => {
    if (inputAreaValue.length > INPUT_MAX_LENGTH) {
      setHasLimitError(true);
    } else {
      setHasLimitError(false);
    }
  }, [inputAreaValue]);

  const title = (
    <Heading ellipsis size="large">
      {t<TLocalesKeys>('answer_feedback_explanation_modal_title')}
    </Heading>
  );

  const content = (
    <Box paddingTop="3px" direction="vertical">
      <FormField
        {...(hasLimitError
          ? {
              status: 'error',
              statusMessage: (
                <Text size="small" skin="error">
                  {t<TLocalesKeys>('answer_feedback_explanation_modal_input_limit_error')}
                </Text>
              ),
            }
          : {})}
      >
        <InputArea
          maxLength={INPUT_MAX_LENGTH + 1}
          autoFocus
          placeholder={t<TLocalesKeys>('answer_feedback_explanation_modal_input_placeholder')}
          rows={4}
          value={inputAreaValue}
          onChange={handleInputChange}
          dataHook={AnswerFeedbackDataHooks.otherReasonInputArea()}
        />
      </FormField>
    </Box>
  );

  const secondaryButtonText = t<TLocalesKeys>('answer_feedback_explanation_modal_cancel_cta');
  const primaryButtonText = t<TLocalesKeys>('answer_feedback_explanation_modal_send_cta');

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} scrollable={false} scrollableContent>
      {isSmallScreen ? (
        <ModalMobileLayout
          onCloseButtonClick={onClose}
          title={title}
          content={content}
          footer={
            <Box align="right" gap={2}>
              <Button
                priority="secondary"
                onClick={onClose}
                dataHook={AnswerFeedbackDataHooks.otherReasonModalCancelButton()}
              >
                {secondaryButtonText}
              </Button>
              <Button
                onClick={handleModalSubmit}
                disabled={primaryButtonProps.disabled}
                dataHook={AnswerFeedbackDataHooks.otherReasonModalSendButton()}
              >
                {primaryButtonText}
              </Button>
            </Box>
          }
        />
      ) : (
        <MessageModalLayout
          onCloseButtonClick={onClose}
          primaryButtonOnClick={handleModalSubmit}
          primaryButtonText={primaryButtonText}
          primaryButtonProps={primaryButtonProps}
          secondaryButtonText={secondaryButtonText}
          secondaryButtonOnClick={onClose}
          title={title}
          content={content}
        />
      )}
    </Modal>
  );
});
